import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Eye, EyeOff, Smartphone, Mail, Lock, ArrowRight, CheckCircle, Loader2 } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { toast } from 'sonner'

import { useAuth } from '@/context/auth-context'

export default function LoginPage() {
    const navigate = useNavigate()
    const { login } = useAuth()
    const [loginMethod, setLoginMethod] = useState<'password' | 'otp'>('password')
    const [isLoading, setIsLoading] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [otpSent, setOtpSent] = useState(false)

    // Form States
    const [emailOrMobile, setEmailOrMobile] = useState('')
    const [password, setPassword] = useState('')
    const [mobile, setMobile] = useState('')
    const [otp, setOtp] = useState('')

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)

        try {
            if (loginMethod === 'password') {
                // Mock credentials check (would be real API call)
                if (emailOrMobile === 'a@a' && password === 'a') {
                    await login('admin', emailOrMobile)
                    toast.success('Welcome back, Admin!')
                    navigate('/admin/dashboard')
                } else if (emailOrMobile === 'm@m' && password === 'm') {
                    await login('mentor', emailOrMobile)
                    toast.success('Welcome back, Mentor!')
                    navigate('/mentor/dashboard')
                } else if (emailOrMobile === 'sa@sa' && password === 'sa') {
                    await login('sub-admin', emailOrMobile)
                    toast.success('Welcome back, Sub Admin!')
                    navigate('/sub-admin/dashboard')
                } else if (emailOrMobile === 'c@c' && password === 'c') {
                    await login('counsellor', emailOrMobile)
                    toast.success('Welcome back, Counsellor!')
                    navigate('/counsellor/dashboard')
                } else {
                    // Default to student
                    await login('student', emailOrMobile)
                    toast.success('Welcome back, Student!')
                    navigate('/dashboard')
                }
            } else {
                // OTP Login
                if (otp === '1234') {
                    await login('student', mobile)
                    toast.success('Login Successful!')
                    navigate('/dashboard')
                } else {
                    toast.error('Invalid OTP (Try 1234)')
                    setIsLoading(false)
                }
            }
        } catch (error) {
            console.error(error)
            toast.error('Login Failed')
            setIsLoading(false)
        }
    }

    const handleSendOtp = () => {
        if (!mobile || mobile.length < 10) {
            toast.error('Please enter a valid mobile number')
            return
        }
        setIsLoading(true)
        setTimeout(() => {
            setIsLoading(false)
            setOtpSent(true)
            toast.success('OTP sent to your mobile number')
        }, 1000)
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
            {/* Background Elements */}


            <GlassmorphicCard className="w-full max-w-md p-8 relative z-10">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-display font-bold text-theme-primary mb-2">Welcome Back</h1>
                    <p className="text-theme-secondary">Login to access your premium courses</p>
                </div>

                {/* Login Method Tabs */}
                <div className="flex p-1 bg-slate-100 dark:bg-slate-800/50 rounded-xl mb-8">
                    <button
                        onClick={() => setLoginMethod('password')}
                        className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-300 ${loginMethod === 'password'
                            ? 'bg-white dark:bg-slate-700 text-primary-blue shadow-sm'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                            }`}
                    >
                        Password Login
                    </button>
                    <button
                        onClick={() => setLoginMethod('otp')}
                        className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-300 ${loginMethod === 'otp'
                            ? 'bg-white dark:bg-slate-700 text-primary-blue shadow-sm'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                            }`}
                    >
                        OTP Login
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleLogin} className="space-y-4">
                    <AnimatePresence mode="wait">
                        {loginMethod === 'password' ? (
                            <motion.div
                                key="password-form"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.2 }}
                                className="space-y-4"
                            >
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-theme-secondary">Email or Mobile</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                        <input
                                            type="text"
                                            value={emailOrMobile}
                                            onChange={(e) => setEmailOrMobile(e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue"
                                            placeholder="Enter your email or mobile"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <label className="text-sm font-medium text-theme-secondary">Password</label>
                                        <button
                                            type="button"
                                            onClick={() => toast.success('Password reset link sent to your email/mobile')}
                                            className="text-xs text-primary-blue hover:underline"
                                        >
                                            Forgot Password?
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full pl-10 pr-10 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue"
                                            placeholder="Enter your password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="otp-form"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.2 }}
                                className="space-y-4"
                            >
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-theme-secondary">Mobile Number</label>
                                    <div className="relative">
                                        <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                        <input
                                            type="tel"
                                            value={mobile}
                                            onChange={(e) => setMobile(e.target.value)}
                                            disabled={otpSent}
                                            className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue disabled:opacity-70"
                                            placeholder="Enter your mobile number"
                                            maxLength={10}
                                        />
                                        {otpSent && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                                                <CheckCircle className="h-5 w-5" />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {!otpSent ? (
                                    <button
                                        type="button"
                                        onClick={handleSendOtp}
                                        disabled={isLoading || !mobile}
                                        className="w-full py-3 bg-primary-blue/10 hover:bg-primary-blue/20 text-primary-blue font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                                    >
                                        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Send OTP'}
                                    </button>
                                ) : (
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-theme-secondary">Enter OTP</label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value)}
                                                className="w-full px-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-primary placeholder-slate-400 text-center font-mono text-lg tracking-widest focus:outline-none focus:border-primary-blue"
                                                placeholder="----"
                                                maxLength={4}
                                            />
                                        </div>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-theme-muted">Didn't receive OTP?</span>
                                            <button type="button" onClick={handleSendOtp} className="text-primary-blue hover:underline">
                                                Resend
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {(loginMethod === 'password' || otpSent) && (
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full py-3 bg-primary-blue hover:bg-blue-600 text-white font-bold rounded-lg glow-primary transition-all duration-300 flex items-center justify-center gap-2 mt-6"
                        >
                            {isLoading ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                                <>
                                    Login Now
                                    <ArrowRight className="h-5 w-5" />
                                </>
                            )}
                        </button>
                    )}
                </form>

                <div className="relative my-8 flex items-center gap-4">
                    <div className="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                    <span className="text-sm text-slate-500">Or continue with</span>
                    <div className="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <button className="py-2.5 px-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-secondary hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2">
                        <svg className="h-5 w-5" viewBox="0 0 24 24">
                            <path
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                fill="#4285F4"
                            />
                            <path
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                fill="#34A853"
                            />
                            <path
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z"
                                fill="#FBBC05"
                            />
                            <path
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                fill="#EA4335"
                            />
                        </svg>
                        Google
                    </button>
                    <button className="py-2.5 px-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-secondary hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2">
                        <Smartphone className="h-5 w-5 text-slate-900 dark:text-white" />
                        Mobile
                    </button>
                </div>

                <div className="mt-8 text-center text-sm">
                    <span className="text-theme-secondary">Don't have an account? </span>
                    <Link to="/signup" className="font-semibold text-primary-blue hover:text-blue-600 transition-colors">
                        Sign Up
                    </Link>
                </div>
            </GlassmorphicCard>
        </div>
    )
}
